import { FC, useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "hooks/useReduxStore";
import dishPortionOrdersSelector from "store/selectors/order/dishPortionOrdersSelector";
import CommonButton from "components/common/ui/CommonButton";
import { foodPurchasingGuideActions } from "store/slices/inventory/foodPurchasingGuideSlice";
import {
  DishOrderPortions,
  FoodCookOrigin,
  ParamFilterPurchaseGuide,
  PurchasingGuideItem,
} from "client/jspPlatformExperiment";
import DataGridListBox from "components/common/ui/DataGridListBox";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRowId,
} from "@mui/x-data-grid";
import foodPurchasingGuideSelector from "store/selectors/inventory/foodPurchasingGuideSelector";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import Routes from "models/routes";
import { useNavigate } from "react-router-dom";
import EditSection from "components/common/ui/EditSection";
import CommonDownloadToolbar from "components/common/ui/DataGridToolbarExport";
import {
  SpacedDropDownSelect,
  SpacedAutocompleteInput,
} from "components/common/ui/SpacedTextInput";
import { Grid, SelectChangeEvent } from "@mui/material";
import { ingredientCategoriesOptions } from "utils/food/foodItemOptionDisplayUtils";

interface FoodPurchasingGuideTableProps {
  onGetPurchaseListClick?: (
    filter_params?: ParamFilterPurchaseGuide | null,
  ) => void;
}

const FoodPurchasingGuideTable: FC<FoodPurchasingGuideTableProps> = ({
  onGetPurchaseListClick,
}) => {
  const dishPortionOrders = useAppSelector(
    dishPortionOrdersSelector.allDishPortionOrders,
  );
  const purchasingGuide = useAppSelector(
    foodPurchasingGuideSelector.foodPurchasingGuide,
  );
  const purchasingGuideIsLoading = useAppSelector(
    foodPurchasingGuideSelector.foodPurchasingGuideIsLoading,
  );
  const [filteredPurchasingGuide, setFilteredPurchasingGuide] = useState<
    PurchasingGuideItem[]
  >(purchasingGuide ?? []);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [cookOrigin, setCookOrigin] = useState<FoodCookOrigin | "All">(
    FoodCookOrigin.ROBOT,
  );
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [filterAmountGreaterThan, setFilterAmountGreaterThan] =
    useState<number>(0);

  useEffect(() => {
    setFilteredPurchasingGuide(
      purchasingGuide?.filter(
        (item) => item.required_amount >= filterAmountGreaterThan,
      ) ?? [],
    );
  }, [purchasingGuide, filterAmountGreaterThan]);

  const defaultGetPurchaseListClick = useCallback(() => {
    const portionOrders = Object.values(dishPortionOrders)
      .filter(
        (displayPortionOrder) =>
          !!displayPortionOrder.food_id && displayPortionOrder.amount > 0,
      )
      .map((portionOrder) => portionOrder);
    dispatch(
      foodPurchasingGuideActions.fetchFoodPurchasingGuide({
        required_dish_portion_orders: portionOrders as DishOrderPortions[],
        reserved_dish_portion_orders: undefined,
        filter_params: {
          food_cook_origin_any: cookOrigin !== "All" ? [cookOrigin] : undefined,
          food_categories:
            selectedCategories.length > 0 ? selectedCategories : undefined,
        },
      }),
    );
  }, [dishPortionOrders, dispatch, cookOrigin, selectedCategories]);

  const handleGetPurchaseListClick = useCallback(() => {
    if (onGetPurchaseListClick) {
      onGetPurchaseListClick({
        food_cook_origin_any: cookOrigin !== "All" ? [cookOrigin] : undefined,
        food_categories:
          selectedCategories.length > 0 ? selectedCategories : undefined,
      });
    } else {
      defaultGetPurchaseListClick();
    }
  }, [
    onGetPurchaseListClick,
    defaultGetPurchaseListClick,
    cookOrigin,
    selectedCategories,
  ]);

  const handleCookOriginChange = (
    event: SelectChangeEvent<FoodCookOrigin | "All">,
  ): void => {
    dispatch(foodPurchasingGuideActions.setFoodPurchasingGuide(null));
    const { value } = event.target;
    setCookOrigin(value === "All" ? "All" : (value as FoodCookOrigin));
  };

  const handleFilterAmountGreaterThanChange = (
    event: SelectChangeEvent<number>,
  ): void => {
    const { value } = event.target;
    const newThreshold = Number(value);
    if (newThreshold || newThreshold === 0) {
      setFilterAmountGreaterThan(newThreshold);
    }
  };

  /**
   * Go to ingredient stock change details page
   * @param id The grid row ID which should be the ingredient's food ID
   */
  const handleViewDetailsClick = (id: GridRowId) => () => {
    navigate(`${Routes.INVENTORY_DETAILS}/${id}`, {
      replace: false,
      state: { foodId: id },
    });
  };

  const columns: GridColDef[] = [
    {
      field: "editAction",
      type: "actions",
      width: 45,
      cellClassName: "actions",
      getActions: ({ id }) => {
        const to = `${Routes.INVENTORY_DETAILS}/${id}`;
        return [
          <GridActionsCellItem
            key={id}
            icon={<FolderOpenIcon />}
            label="View details"
            className="textPrimary"
            onClick={(e) => {
              // Handle Ctrl/Cmd + click to open in new tab
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                window.open(to, "_blank");
              } else {
                handleViewDetailsClick(id)();
              }
            }}
            onMouseDown={(e) => {
              // Handle middle-click (wheel click) to open in new tab
              if (e.button === 1) {
                e.preventDefault();
                window.open(to, "_blank");
              }
            }}
            color="inherit"
          />,
        ];
      },
    },
    { field: "food_id", headerName: "ID", width: 60, editable: false },
    {
      field: "supplier_product_code",
      headerName: "GFS code",
      flex: 30,
      editable: false,
      renderCell: (params) => (
        <a
          href={`#${params.value}`}
          style={{ color: "inherit" }}
          onClick={(e) => {
            e.preventDefault();
            // Navigate to GFS product page
            window.open(
              `https://order.gfs.com/product/${params.value}`,
              "_blank",
            );
          }}
        >
          {params.value}
        </a>
      ),
    },
    { field: "food_name", headerName: "Name", flex: 180, editable: false },
    {
      field: "required_amount",
      headerName: "Require",
      flex: 60,
      editable: false,
      valueFormatter: (value: number) => `${value.toLocaleString()} g `,
    },
    {
      field: "reserved_amount",
      headerName: "Reserved",
      flex: 60,
      editable: false,
      valueFormatter: (value: number) => `${value.toLocaleString()} g `,
    },
    {
      field: "stock_level",
      headerName: "Stock",
      flex: 60,
      editable: false,
      valueFormatter: (value: number) => `${value.toLocaleString()} g `,
    },
    {
      field: "to_buy_amount",
      headerName: "To buy",
      flex: 60,
      editable: false,
      valueFormatter: (value: number) => `${value.toLocaleString()} g `,
    },
    {
      field: "to_buy_boxes",
      headerName: "To buy (Boxes)",
      flex: 60,
      editable: false,
      valueFormatter: (value: number) =>
        `${value.toLocaleString()} ${value > 1 ? "boxes" : "box"} `,
    },
  ];

  return (
    <>
      <EditSection>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SpacedDropDownSelect<FoodCookOrigin | "All">
              options={{
                All: "All",
                Robot: FoodCookOrigin.ROBOT,
                Manual: FoodCookOrigin.MANUAL,
              }}
              label="Ingredients for"
              value={cookOrigin}
              onChange={handleCookOriginChange}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SpacedAutocompleteInput
              multiple
              options={ingredientCategoriesOptions}
              label="Category"
              value={selectedCategories}
              onChange={(_event, newValue) => {
                dispatch(
                  foodPurchasingGuideActions.setFoodPurchasingGuide(null),
                );
                setSelectedCategories(newValue as string[]);
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SpacedDropDownSelect<number>
              options={{
                "0 g": 0,
                "1 kg": 1000,
                "5 kg": 5000,
              }}
              label="Amount greater than"
              value={filterAmountGreaterThan}
              onChange={handleFilterAmountGreaterThanChange}
              size="small"
            />
          </Grid>
        </Grid>
        <CommonButton
          text="Get purchase list"
          onClick={handleGetPurchaseListClick}
        />
      </EditSection>
      <DataGridListBox>
        <DataGrid
          rows={filteredPurchasingGuide ?? purchasingGuide ?? undefined}
          columns={columns}
          editMode="row"
          getRowId={(row: PurchasingGuideItem) => row.food_id}
          loading={purchasingGuideIsLoading}
          slots={{
            toolbar: CommonDownloadToolbar,
          }}
          initialState={{
            columns: {
              columnVisibilityModel: {
                food_id: false,
                supplier_product_code: true,
                reserved_amount: false,
                stock_level: false,
                to_buy_amount: false,
              },
            },
            sorting: {
              sortModel: [
                {
                  field: "required_amount",
                  sort: "desc",
                },
              ],
            },
          }}
        />
      </DataGridListBox>
    </>
  );
};

export default FoodPurchasingGuideTable;
